// ============================================================
// Invoicing module manifest — A2 (2026-04-25).
//
// Closes R4 deferral: the Services pack now ships customer master +
// customer invoicing WITHOUT requiring inventory. See DESIGN_LOG entry
// "2026-04-25 — A2 Sales→Invoicing split" for the full debate.
//
// Owners: principal-architect (manifest contract + supersededBy field) +
//         firebase-expert (Vite re-export wiring) +
//         erp-operations (sales lifecycle integrity preserved by leaving
//                         the data layer in sales/) +
//         ux-architect (sidebar implications for Services tenants).
//
// What this is the source of truth for
// ─────────────────────────────────────
//   - 2 sidebar routes in 'sell' section: Customers + Customer Invoices
//     (a strict subset of Sales' 5 routes — no Quotation, no SO, no DO)
//   - 2 Firestore collections (customers + customerinvoices) — SHARED
//     with Sales. Both modules write to the same collections; auth-security
//     reconciles writes through `hasPerm('sales.write') || hasPerm('invoicing.write')`
//     in firestore.rules.
//   - 2 permission strings (invoicing.read, invoicing.write)
//   - 1 dashboard widget descriptor (unpaid customer invoices)
//   - Fits ALL four industry packs — but only Services enables it BY DEFAULT.
//     Manufacturing/Trading/Retail get sales which supersedes invoicing.
//
// supersededBy: ['sales']
// ────────────────────────
//   When a tenant has BOTH 'sales' AND 'invoicing' enabled, sales wins
//   the customers + customer-invoices routes. Invoicing's bundle still
//   loads (it's tiny and shares chunks with sales) but its routes are
//   dropped from the sidebar by registry.getRoutesFor(). See DESIGN_LOG
//   A2 sub-decision 2 for the why (UX rejects two "Customers" sidebar items).
//
// IMPORTANT: changes to `id` are BREAKING (tenantConfig.enabledModules,
// permissions, URL hashes all reference it).
// ============================================================

export default {
  id:          'invoicing',
  name:        'Invoicing',
  version:     '1.0.0',
  description: 'Customer master + direct invoicing (no inventory). For service-based businesses.',

  // Lazy load — Vite emits a chunk; tenants without 'invoicing' enabled
  // never download it. The chunk is small because most code re-exports
  // from sales' shared chunks (customer-invoice-modal, customer-modal,
  // invoice-print-view, customers data layer).
  load: () => import('./index.js'),

  // Sidebar contributions — 'sell' section (same as sales).
  // A Services tenant sees only these two; a Manufacturing tenant sees
  // sales' five (these are dropped via supersededBy).
  // 2026-05-04 sidebar-leak fix — gate on `invoicing.read`. The Services
  // pack's `consultant` role has invoicing.* (so they see both routes);
  // director + admin pick this up via __ALL_EXCEPT_TENANT_CONFIG__. Staff
  // does NOT get invoicing.read.
  routes: [
    {
      id:       'customers',
      label:    'Customers',
      icon:     '👥',
      section:  'sell',
      sidebarPosition: 10,
      visible:  (role, hasPerm) => hasPerm('invoicing.read'),
      mount:    'mountCustomersList',
      unmount:  'unmountCustomersList'
    },
    {
      id:       'customer-invoices',
      label:    'Customer Invoices',
      icon:     '🧾',
      section:  'sell',
      sidebarPosition: 50,
      visible:  (role, hasPerm) => hasPerm('invoicing.read'),
      mount:    'mountInvoicingInvoicesList',
      unmount:  'unmountInvoicingInvoicesList'
    }
  ],

  // Firestore collections this module reads/writes. SHARED with sales —
  // both modules' writes land in the same docs. Rules accept either
  // sales.write or invoicing.write to gate (see firestore.rules).
  schemas: [
    'customers',
    'customerinvoices'
  ],

  // Permission strings the module declares. Distinct namespace from
  // sales — a Services tenant's `consultant` role gets `invoicing.write`
  // (not `sales.write`) because they should NOT inherit access to
  // sales-only routes (Quotations / SOs / DOs) the day a director also
  // enables sales on their tenant.
  permissions: [
    'invoicing.read',
    'invoicing.write'
  ],

  // No dependencies — that's the whole point. Services tenants don't
  // ship inventory. Registry boot validation lets this module load on
  // a tenant whose enabledModules = ['dashboard','hr','leave','payroll',
  // 'account','settings','invoicing'].
  dependencies: [],

  // R3 — when both sales and invoicing are enabled on the same tenant,
  // sales is the SUPERSET. Registry's getRoutesFor() drops invoicing's
  // route declarations (the sales routes provide everything invoicing
  // would have offered + more). The invoicing bundle still loads in case
  // future non-route exports surface; today this is a no-op.
  supersededBy: ['sales'],

  // Fits all four packs, but defaults are pack-controlled. Services
  // pack ENABLES this by default; the others ship sales instead.
  fitsIndustries: ['services', 'manufacturing', 'trading', 'retail'],

  // No Cloud Functions — direct client writes via the same data layer
  // sales uses (createInvoice in customer-invoices.js).
  functions: [],

  // Dashboard widget contributions — DEFERRED (silent-sweep 2026-04-26):
  // mount function 'mountInvoicingUnpaidInvoicesWidget' was never built.
  // Declaring it here causes dashboard.js to log a console.warn on every
  // load. Stub-out to silence; uncomment + add widgets.js when ready.
  // R3+R4 dashboard-composition will dedupe duplicate KPIs (vs sales' equivalent).
  dashboardWidgets: []
  // [
  //   {
  //     id:           'invoicing-unpaid-invoices',
  //     label:        'Unpaid customer invoices',
  //     section:      'kpi',
  //     order:        50,
  //     defaultRoles: ['director', 'admin', 'accounts', 'consultant']
  //   }
  // ]
};
